import {
  Pagination as MuiPagination,
  TablePagination as MuiTablePagination,
  PaginationItem,
  type PaginationProps,
  type TablePaginationProps,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Box, Typography, useTheme } from 'hephaestus/web';
import type { ChangeEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem } from '../menuItem/menuItem';
import { SelectField } from '../selectField/selectField';
import { usePaginationTokens } from './pagination.tokens';
import { usePaginationSx } from './usePaginationSx';

export type PaginationVariant = 'default' | 'tableFooter';

interface IPaginationProps extends PaginationProps {
  perPage?: number;
  total?: number;
  onChangePerPage?: (perPage: number) => void;
  showPerPage?: boolean;
  pageSizes?: number[];
  /** i18n key for the “start–end of total” label (default: global lists/tables). */
  summaryKey?: string;
  /** i18n namespace for {@link summaryKey} (default `shared`). */
  summaryNamespace?: string;
  /**
   * `default`: legacy layout (standalone pages and most {@link DataTable} footers).
   * `tableFooter`: full-width bar with first/last buttons — opt in (e.g. recon flags).
   */
  ovVariant?: PaginationVariant;
}

export const DEFAULT_PAGE_SIZES = [15, 25, 50, 100];

const normalizePaginationTotal = (total?: number): number => {
  if (typeof total !== 'number' || !Number.isFinite(total) || total < 0) {
    return 0;
  }
  return total;
};

const paginationRenderItem = (
  item: Parameters<NonNullable<PaginationProps['renderItem']>>[0],
) => (
  <PaginationItem
    {...item}
    data-testid={
      item.type === 'page'
        ? `pagination-button-${item.page}`
        : `pagination-${item.type}-button`
    }
  />
);

export const Pagination = ({
  perPage = 15,
  onChangePerPage,
  total,
  showPerPage = true,
  pageSizes = DEFAULT_PAGE_SIZES,
  summaryKey = 'paginationSummary',
  summaryNamespace,
  ovVariant = 'default',
  ...props
}: IPaginationProps) => {
  const summaryNs = summaryNamespace ?? 'shared';
  const i18nNamespaces =
    summaryNs !== 'shared' ? [summaryNs, 'shared'] : ['shared'];
  const { t } = useTranslation(i18nNamespaces);
  const { t: tShared } = useTranslation('shared');

  const page = props.page || 1;
  const offSet = perPage * (page - 1);
  const safeTotal = normalizePaginationTotal(total);
  const showSummary = safeTotal > 0;
  const summaryStart = showSummary ? offSet + 1 : 0;
  const summaryEnd = showSummary ? Math.min(offSet + perPage, safeTotal) : 0;
  const summaryLabel = t(summaryKey, {
    start: summaryStart,
    end: summaryEnd,
    total: safeTotal,
  });

  const paginationSx = usePaginationSx(props.sx, ovVariant);

  const perPageSelect =
    showPerPage &&
    perPage &&
    onChangePerPage &&
    (ovVariant === 'tableFooter' ? (
      <TableFooterPerPageSelect
        perPage={perPage}
        pageSizes={pageSizes}
        onChangePerPage={onChangePerPage}
        disabled={props.disabled}
      />
    ) : (
      <SelectField
        testId='pagination-per-page-select'
        label=''
        value={perPage}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChangePerPage(Number(e.target.value))
        }
        size='small'
        sx={{ p: 0 }}
        disabled={props.disabled}
      >
        {pageSizes.map((size) => (
          <MenuItem
            key={size}
            testId={`pagination-per-page-select-${size}-menu-item`}
            value={size}
          >
            {tShared('perPageWithSize', { size })}
          </MenuItem>
        ))}
      </SelectField>
    ));

  if (ovVariant === 'default') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: showSummary ? 'space-between' : 'end',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        {showSummary && (
          <Typography variant='bodySmall'>{summaryLabel}</Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          {perPageSelect}
          <MuiPagination
            {...props}
            sx={paginationSx}
            data-testid='pagination'
            renderItem={paginationRenderItem}
          />
        </Box>
      </Box>
    );
  }

  return (
    <TableFooterPaginationLayout
      showSummary={showSummary}
      summaryLabel={summaryLabel}
      perPageSelect={perPageSelect}
      paginationProps={props}
      paginationSx={paginationSx}
    />
  );
};

type TableFooterPerPageSelectProps = {
  perPage: number;
  pageSizes: number[];
  onChangePerPage: (perPage: number) => void;
  disabled?: boolean;
};

const TableFooterPerPageSelect = ({
  perPage,
  pageSizes,
  onChangePerPage,
  disabled,
}: TableFooterPerPageSelectProps) => {
  const { t } = useTranslation('shared');
  const theme = useTheme();
  const tokens = usePaginationTokens(theme);

  const outline = theme.color.outlineVariant ?? theme.color.outline;
  const surface = theme.color.surface ?? '#fff';
  const muted = theme.color.onSurfaceVariant ?? theme.color.onSurface;

  const perPageSx = {
    p: 0,
    minWidth: 72,
    ...(tokens.perPageSelect && {
      '& .MuiOutlinedInput-root': {
        borderRadius: tokens.perPageSelect.borderRadius
          ? `${tokens.perPageSelect.borderRadius} !important`
          : '6px !important',
        backgroundColor: tokens.perPageSelect.backgroundColor
          ? `${tokens.perPageSelect.backgroundColor} !important`
          : `${surface} !important`,
        '& fieldset': {
          borderColor: tokens.perPageSelect.borderColor ?? outline,
          borderWidth: tokens.perPageSelect.borderWidth ?? '1px',
        },
      },
      '& .MuiSelect-select': {
        color: tokens.perPageSelect.color
          ? `${tokens.perPageSelect.color} !important`
          : `${muted} !important`,
        fontSize: tokens.perPageSelect.fontSize
          ? `${tokens.perPageSelect.fontSize} !important`
          : '14px !important',
        fontWeight: tokens.perPageSelect.fontWeight
          ? `${tokens.perPageSelect.fontWeight} !important`
          : '500 !important',
        py: '6px',
      },
      '& .MuiSelect-icon': {
        color: tokens.perPageSelect.color ?? muted,
      },
    }),
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Typography
        variant='bodySmall'
        sx={{ color: muted, whiteSpace: 'nowrap' }}
      >
        {t('rowsPerPage')}
      </Typography>
      <SelectField
        testId='pagination-per-page-select'
        label=''
        value={perPage}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChangePerPage(Number(e.target.value))
        }
        size='small'
        sx={perPageSx}
        disabled={disabled}
        renderValue={(selected: unknown) => String(selected)}
      >
        {pageSizes.map((size) => (
          <MenuItem
            key={size}
            testId={`pagination-per-page-select-${size}-menu-item`}
            value={size}
          >
            {String(size)}
          </MenuItem>
        ))}
      </SelectField>
    </Box>
  );
};

type TableFooterPaginationLayoutProps = {
  showSummary: boolean;
  summaryLabel: string;
  perPageSelect: ReactNode;
  paginationProps: PaginationProps;
  paginationSx: SxProps<Theme>;
};

const TableFooterPaginationLayout = ({
  showSummary,
  summaryLabel,
  perPageSelect,
  paginationProps,
  paginationSx,
}: TableFooterPaginationLayoutProps) => {
  const theme = useTheme();
  const surface = theme.color.surface ?? '#fff';
  const barBg =
    theme.color.surfaceContainer ??
    theme.color.surfaceContainerVariant ??
    surface;
  const muted = theme.color.onSurfaceVariant ?? theme.color.onSurface;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: showSummary ? 'space-between' : 'end',
        alignItems: 'center',
        flexWrap: 'wrap',
        rowGap: 1,
        columnGap: 2,
        width: '100%',
        boxSizing: 'border-box',
        px: 2,
        py: 1.5,
        backgroundColor: barBg,
      }}
    >
      {showSummary && (
        <Typography variant='bodySmall' sx={{ color: muted }}>
          {summaryLabel}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          ml: showSummary ? undefined : 'auto',
        }}
      >
        {perPageSelect}
        <MuiPagination
          {...paginationProps}
          sx={paginationSx}
          data-testid='pagination'
          showFirstButton
          showLastButton
          shape='rounded'
          renderItem={paginationRenderItem}
        />
      </Box>
    </Box>
  );
};

export const TablePagination = (props: TablePaginationProps) => {
  const { t } = useTranslation('shared');
  return (
    <MuiTablePagination
      labelRowsPerPage={t('rowsPerPage')}
      labelDisplayedRows={({ from, to, count }) =>
        t('paginationSummary', {
          start: from,
          end: to,
          total: count !== -1 ? count : `${to}+`,
        })
      }
      {...props}
    />
  );
};
